import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { deletePackage, searchPackage } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppTable from '../../components/AppTable/AppTable';
import { setPagination } from '../../redux/Actions/TableAction';
import DeleteButton from '../../components/Delete/DeleteButton';
import EditButton from '../../components/Edit/EditButton';
import toast from 'react-hot-toast';
import TableSearch from '../../components/TableSearch/TableSearch';
import { hasPermission } from '../../utils/permissionUtils';
import DeleteConfirmationModal from '../../components/CustomModal/DeleteConfirmationModal';

function PackageMaster() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);

    // Search state variables
    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [packageToDelete, setPackageToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Permission checks using hasPermission utility
    const canEditPackage = hasPermission('packages', 'write');
    const canReadPackage = hasPermission('packages', 'read');
    const canDeletePackage = hasPermission('packages', 'delete');
    const canAddPackage = hasPermission('packages', 'write');

    console.log({ canEditPackage, canReadPackage, canDeletePackage, canAddPackage });

    useEffect(() => {
        if (canReadPackage) {
            fetchPackages();
        }
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit, canReadPackage]);

    const fetchPackages = (searchKey, searchValue) => {
        if (!canReadPackage) {
            toast.error('You do not have permission to view packages');
            return;
        }

        setIsSearching(true);

        const searchObj = {};

        // Only add search key/value if both are provided
        if (searchKey && searchValue) {
            searchObj[searchKey] = searchValue;
        }

        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: searchObj
        };

        HitApi(json, searchPackage).then((res) => {
            console.log("res______", res);

            if (res?.statusCode == 200) {
                const packageData = Array.isArray(res?.data.docs) ? res?.data.docs : [];
                dispatch(setApiJson(packageData));

                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };

                dispatch(setPagination(paginationData));
            } else {
                dispatch(setApiJson([]));
                toast.error('Failed to fetch packages. Please try again.');
            }
        }).catch(error => {
            dispatch(setApiJson([]));
            toast.error('Error fetching packages. Please try again.');
        }).finally(() => {
            setIsSearching(false);
        });
    };

    // Search functionality
    const handleSearch = () => {
        if (!canReadPackage) {
            toast.error('You do not have permission to search packages');
            return;
        }

        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value');
            return;
        }
        fetchPackages(optionSelect, valueSearched.trim());
    };

    // Clear search functionality
    const handleClearSearch = () => {
        if (!canReadPackage) {
            toast.error('You do not have permission to access packages');
            return;
        }

        setOptionSelect('');
        setValueSearched('');
        fetchPackages();
    };

    // Search options based on table keys
    const options = [
        { label: 'Package Name', value: 'name' },
        { label: 'Description', value: 'description' },
        { label: 'Status', value: 'isActive' },
    ];

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toFixed(2)}`;
    };

    const handleEdit = (row) => {
        if (!canEditPackage) {
            toast.error('You do not have permission to edit packages');
            return;
        }
        navigate(`/packages/edit/${row._id}`);
    };

    // Updated handleDelete to show modal instead of window.confirm
    const handleDelete = (row) => {
        if (!canDeletePackage) {
            toast.error('You do not have permission to delete packages');
            return;
        }

        setPackageToDelete(row);
        setShowDeleteModal(true);
    };

    // Handle actual deletion after confirmation
    const confirmDelete = () => {
        if (!packageToDelete) return;

        setIsDeleting(true);

        const json = {
            _id: packageToDelete._id
        };

        HitApi(json, deletePackage).then((res) => {
            if (res?.statusCode === 200) {
                toast.success('Package deleted successfully!');
                fetchPackages();
                setShowDeleteModal(false);
                setPackageToDelete(null);
            } else {
                toast.error('Failed to delete package. Please try again.');
            }
        }).catch(error => {
            toast.error('Error occurred while deleting package. Please try again.');
        }).finally(() => {
            setIsDeleting(false);
        });
    };

    // Handle modal close
    const handleCloseModal = () => {
        if (!isDeleting) {
            setShowDeleteModal(false);
            setPackageToDelete(null);
        }
    };

    const renderStatus = (value, row) => {
        const status = row.isActive;

        if (status === undefined || status === null) return null;

        const isActive = status === true;
        const bgColor = isActive ? "bg-green-100" : "bg-red-100";
        const textColor = isActive ? "text-green-800" : "text-red-800";
        const label = isActive ? "Active" : "Inactive";

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {label}
            </span>
        );
    };

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

    const baseTableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "center",
            render: (_, row, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Package Name",
            key: "name",
            render: (value) => (
                <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-2">
                        <span className="text-sm font-medium text-purple-600">
                            {value && value.charAt(0).toUpperCase()}
                        </span>
                    </div>
                    <span className="font-medium">{value || 'N/A'}</span>
                </div>
            )
        },
        {
            title: "Description",
            key: "description",
            render: (value) => (
                <div className="max-w-xs truncate">
                    <span className="text-sm text-gray-600">
                        {value || 'N/A'}
                    </span>
                </div>
            )
        },
        {
            title: "Services",
            key: "services",
            render: (value) => (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                    {Array.isArray(value) ? value.length : 0} services
                </span>
            )
        },
        {
            title: "Actual Price",
            key: "totalActualPrice",
            render: (value) => (
                <span className="text-sm text-gray-900 font-medium">
                    {formatCurrency(value)}
                </span>
            )
        },
        {
            title: "Package Price",
            key: "packagePrice",
            render: (value) => (
                <span className="text-sm text-green-700 font-semibold">
                    {formatCurrency(value)}
                </span>
            )
        },
        {
            title: "Discount",
            key: "discountPercentage",
            render: (value) => (
                <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                    {parseFloat(value || 0).toFixed(1)}%
                </span>
            )
        },
        {
            title: "Validity",
            key: "validity",
            render: (value) => (
                <span className="text-sm text-gray-600">
                    {value ? `${value.value} ${value.unit}` : 'N/A'}
                </span>
            )
        },
        {
            title: "Status",
            key: "isActive",
            render: renderStatus
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (value, row) => {
                // Check if user has any action permissions
                const hasAnyPermission = canEditPackage || canDeletePackage;

                if (!hasAnyPermission) {
                    return (
                        <div className="flex justify-center">
                            <span className="text-sm text-gray-400">No actions available</span>
                        </div>
                    );
                }

                return (
                    <div className="flex justify-center space-x-2">
                        {canEditPackage && (
                            <EditButton
                                onEdit={() => handleEdit(row)}
                                itemName="package"
                                tooltip="Edit Package"
                            />
                        )}
                        {canDeletePackage && (
                            <DeleteButton
                                onDelete={() => handleDelete(row)}
                                itemName="package"
                                tooltip="Delete Package"
                            />
                        )}
                    </div>
                );
            }
        }
    ];

    // Only show actions column if user has any action permissions
    const shouldShowActionsColumn = canEditPackage || canDeletePackage;
    const tableHeaders = shouldShowActionsColumn
        ? baseTableHeaders
        : baseTableHeaders.filter(header => header.key !== 'actions');

    // Show access denied message if user doesn't have read permission
    if (!canReadPackage) {
        return (
            <div className="p-5">
                <PageHeader
                    title={'Package Management'}
                    description={'Manage service packages and combo deals'}
                />
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <h3 className="text-lg font-medium text-red-600 mb-2">Access Denied</h3>
                        <p className="text-gray-600">You don't have permission to view packages.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-5">
            <PageHeader
                title={'Package Management'}
                description={'Manage service packages and combo deals'}
            />
            <AppTable
                TH={tableHeaders}
                TD={Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []}
                isLoading={!Array.isArray(ApiReducer.apiJson) || ApiReducer.apiJson.length === 0 || isSearching}
                emptyMessage="No packages found"
                buttonText={canAddPackage ? "Add Package" : undefined}
                navigateTo={canAddPackage ? "/packages/add" : undefined}
                TableSearch={
                    <TableSearch
                        options={options}
                        onClick={handleSearch}
                        optionSelect={optionSelect}
                        setOptionSelect={setOptionSelect}
                        setValueSearched={setValueSearched}
                        valueSearched={valueSearched}
                        onClear={handleClearSearch}
                    />
                }
            />
            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                onClose={handleCloseModal}
                onConfirm={confirmDelete}
                itemName={packageToDelete?.name}
                itemType="package"
                isLoading={isDeleting}
            />
        </div>
    );
}

export default PackageMaster;
